import React, { useState } from "react";
import Table from "components/tables/table";
import AcceptModal from "components/modals/acceptModal";
import { WrapperSeamless } from "layout-components";
import { useGetList } from "hooks";
import * as Api from "service";
import { Button } from "@material-ui/core";
import useStore from "context";
import { useTranslation } from "localization/translation";
import { ACCEPT, PENDING, REJECT, DECLINED } from "Constants";
import { ChangesDetailsModal } from "components/modals/detailsModal";
import { dateFormatter } from "helpers/dateFormatter";
import {
  getChangedGoodKeys,
  getChangedGoodsFromAfterBefore,
} from "helpers/getChangedGoodKeys";

const limit = 20;
const isPageCorrect = true;
const statuses = {
  pending: "requested",
  accepted: "confirmed",
  declined: "canceled",
};

const GoodsChanges = () => {
  const { setErrorMsg, language } = useStore();
  const translation = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [goodDetail, setGoodDetail] = useState(null);
  const [selectedRequestData, setSelectedRequestData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [afterData, setAfterData] = useState(null);
  const [beforeData, setBeforeData] = useState(null);

  const [query, setQuery] = useState({
    sort: "created_at",
    order: "desc",
  });

  const [items, total, refresh, setItems] = useGetList(
    Api.goodsChangesRequests.getAll,
    query,
    limit,
    isPageCorrect
  );

  const openModal = (good, type) => {
    const { after, before } = getChangedGoodKeys(good);
    setAfterData(after);
    setBeforeData(before);

    const text =
      type === REJECT
        ? `${translation.want_to_reject}`
        : `${translation.want_to_accept}`;
    const status = type ?? ACCEPT;
    const data = {
      text,
      goods: {
        goodRequestId: good?.id,
        status,
      },
    };
    setSelectedRequestData(data);
    setIsOpen(true);
  };

  const openDetailModal = (good) => {
    const { changes } = getChangedGoodKeys(good);

    const afterChanges = getChangedGoodsFromAfterBefore(
      good.after,
      good.before
    );

    setGoodDetail(good.status === PENDING ? changes : afterChanges);
    setIsOpenDetails(true);
  };

  const closeModal = () => {
    setSelectedRequestData(null);
    setIsOpen(false);
  };

  const closeGoodDetail = () => {
    setIsOpenDetails(false);
    setGoodDetail(null);
  };

  const statusAction = (e) => {
    e.preventDefault();
    const { goods } = selectedRequestData;
    const id = goods.goodRequestId;

    setModalLoading(true);
    const data = {
      after: afterData,
      before: beforeData,
    };
    if (goods.status === ACCEPT) {
      Api.main
        .acceptGoodChange(id, data)
        .then((res) => {
          if (res.status) {
            setSelectedRequestData(null);
            setIsOpen(false);
          } else {
            setErrorMsg(res.message);
          }
          refresh();
        })
        .finally(() => {
          setSelectedRequestData(null);
          setIsOpen(false);
          setModalLoading(false);
        });
    } else {
      Api.main
        .rejectGoodChange(id, data)
        .then((res) => {
          if (res.status) {
            setSelectedRequestData(null);
            setIsOpen(false);
          } else {
            setErrorMsg(res.message);
          }
          refresh();
        })
        .finally(() => {
          setSelectedRequestData(null);
          setIsOpen(false);
          setModalLoading(false);
        });
    }
  };

  const rows = items?.map((item) => (
    <>
      <td className="text-left px-3">
        <div className="font-size-sm">{item.company_name}</div>
      </td>
      <td className="text-left px-3">
        <div className="font-size-sm">{item.user_name}</div>
      </td>
      <td className="text-left px-3">
        <div className="font-size-sm">{item[`good_name_${language}`]}</div>
      </td>
      <td className="text-left px-3">
        <div className="font-size-sm">
          {dateFormatter(item.updated_at, "DD-MM-YYYY HH:mm A")}
        </div>
      </td>
      <td className="text-left px-3">
        <div className="font-size-sm text-capitalize">
          {translation[statuses[item.status]]}
        </div>
      </td>
      <td className="text-left px-3">
        <div className="font-size-sm align-box-row">
          {item?.status === PENDING && (
            <>
              <Button
                variant="contained"
                type="button"
                onClick={() => openModal(item, "reject")}
                className="mr-2"
              >
                {translation.reject}
              </Button>
              <Button
                color="secondary"
                type="button"
                onClick={() => openModal(item)}
                variant="contained"
              >
                {translation.accept}
              </Button>
            </>
          )}
        </div>
      </td>
      <td className="text-left px-3">
        <div className="font-size-sm">
          {(item?.status !== DECLINED ||
            (item?.status === DECLINED && !!item?.after)) && (
            <Button
              variant="contained"
              type="button"
              onClick={() => openDetailModal(item)}
              className="mr-2"
            >
              {translation.view}
            </Button>
          )}
        </div>
      </td>
    </>
  ));

  return (
    <WrapperSeamless>
      <Table
        fields={
          <>
            <th className="text-left px-3">
              <span className="mr-3">{translation.company_name}</span>
            </th>
            <th className="text-left px-3">
              <span className="mr-3">{translation.updated_by}</span>
            </th>
            <th className="text-left px-3">
              <span className="mr-3">{translation.goods_name}</span>
            </th>
            <th className="text-left px-3">
              <span className="mr-3">{translation.updated_at}</span>
            </th>
            <th className="text-left px-3">
              <span className="mr-3">{translation.status}</span>
            </th>
            <th className="text-left px-3">
              <span className="mr-3">{translation.actions}</span>
            </th>
            <th className="text-left px-3">
              <span className="mr-3">{translation.details}</span>
            </th>
          </>
        }
        noDrag
        title={translation.goods_changes}
        items={items}
        setItems={setItems}
        total={total?.count}
        rows={rows}
        actions={false}
      />
      <AcceptModal
        isOpen={isOpen}
        close={closeModal}
        accept={statusAction}
        text={selectedRequestData?.text}
        loading={modalLoading}
      />
      <ChangesDetailsModal
        isOpen={isOpenDetails}
        close={closeGoodDetail}
        content={goodDetail}
        text={translation.good_changes_detail}
      />
    </WrapperSeamless>
  );
};

export default GoodsChanges;
